/**
 * All-mode team picker presentation. Renders a search input and a
 * scrollable list of result rows. The Container owns all logic — the View
 * is a pure single-expression renderer.
 *
 * Uses `ScrollView` + `rows.map(...)` (not `FlashList` / `FlatList`): the
 * dataset is capped at 20 items by the Container's `first: 20` query arg,
 * so virtualization isn't needed, and a plain map renders dividers
 * deterministically — FlashList's cell recycling occasionally dropped the
 * divider between adjacent rows that share a name (the user-visible
 * "Barcelona / Barcelona" missing-divider symptom), and `react-native`'s
 * `FlatList` triggered a `CSSStyleDeclaration` indexed-property error in
 * react-native-web in this codebase.
 * @module features/team-selection/components/TeamSelectorAll/TeamSelectorAllView
 */
import { SearchIcon } from "lucide-react-native";
import React, { memo } from "react";
import { ScrollView } from "@/components/atoms/ScrollView";

import { Box } from "@/components/atoms/Box";
import { Input } from "@/components/atoms/Input";
import { Stack } from "@/components/atoms/Stack";

/**
 * One row's data, with its press handler pre-bound by the Container.
 */
export interface TeamSelectorAllRow {
  readonly id: string;
  readonly name: string;
  readonly isActive: boolean;
  /** Pre-formatted "<League> · <flag> <Country>" line, or null when neither is known. */
  readonly subtitle: string | null;
  readonly onPress: () => void;
}

/**
 * Props for {@link TeamSelectorAllView}.
 */
export interface TeamSelectorAllViewProps {
  readonly query: string;
  readonly onChangeQuery: (next: string) => void;
  readonly rows: readonly TeamSelectorAllRow[];
  /** Renders a single row. Owned by the Container so the View can stay pure. */
  readonly renderItem: (item: TeamSelectorAllRow) => React.ReactElement;
  /** Inline placeholder shown when `rows` is empty (idle / loading / error). */
  readonly empty: React.ReactElement;
}

/**
 * All-mode picker view.
 * @param root0 - Component props
 * @param root0.query - Current search input value
 * @param root0.onChangeQuery - Handler invoked when the user types
 * @param root0.rows - Search-result rows; each carries its own `onPress`
 * @param root0.renderItem - Container-owned row renderer
 * @param root0.empty - Inline placeholder shown when `rows` is empty
 * @returns The rendered all-mode picker
 */
const TeamSelectorAllView = ({
  query,
  onChangeQuery,
  rows,
  renderItem,
  empty,
}: TeamSelectorAllViewProps) => (
  <Stack space="3">
    <Input
      variant="outline"
      size="md"
      leadingIcon={SearchIcon}
      field={{
        testID: "team-selector-all-search",
        placeholder: "Search teams...",
        value: query,
        onChangeText: onChangeQuery,
        autoCapitalize: "none",
        autoCorrect: false,
      }}
    />
    <Box className="h-80">
      {rows.length === 0 ? (
        empty
      ) : (
        <ScrollView keyboardShouldPersistTaps="handled">
          {rows.map((item, index) => (
            <React.Fragment key={item.id}>
              {index > 0 ? <Box className="h-px bg-outline-default" /> : null}
              {renderItem(item)}
            </React.Fragment>
          ))}
        </ScrollView>
      )}
    </Box>
  </Stack>
);

TeamSelectorAllView.displayName = "TeamSelectorAllView";

export default memo(TeamSelectorAllView);
